import FeatureGates from "./feature-gate";
import { useConfirmationPage } from "./use-confirmation-page";
import { useJiraTemplateChoice } from "./use-jira-template-choice";
import { useUserSegJiraTemplateRedesignExperiment } from "./use-user-seg-jira-template-redesign-experiment";

import { CreateCloudStatus, useSearchParameters } from "./";
export type ConfirmationPageType =
    | "robots"
    | "jira-animated-provisioning-screen"
    | "tips"
    | "timeout"
    | "user-segmentation"
    | "user-segmentation-redesign"
    | "jira-template-selection"
    | "jira-template-selection-redesign"
    | "error"
    | "redirect";

const useConfirmationPageType = (
    createCloudStatus: CreateCloudStatus,
    deferRedirect: boolean
): ConfirmationPageType => {
    const { shouldShowJiraTemplateChoiceScreen } = useJiraTemplateChoice();
    const { products } = useSearchParameters();
    const {
        userSegmentationContext: { shouldShowUserSegmentationPage }
    } = useConfirmationPage();

    const { getConfirmationPageTypeFromEnrollment } =
        useUserSegJiraTemplateRedesignExperiment();

    if (shouldShowUserSegmentationPage) {
        return getConfirmationPageTypeFromEnrollment(
            "user-segmentation",
            "user-segmentation-redesign"
        );
    }

    if (shouldShowJiraTemplateChoiceScreen) {
        return getConfirmationPageTypeFromEnrollment(
            "jira-template-selection",
            "jira-template-selection-redesign"
        );
    }

    switch (createCloudStatus) {
        case "error":
            return "error";
        case "timeout":
            return "timeout";
        case "success":
            // ATLAS-56326: show JiraAnimatedProvisioningScreen instead of robots.
            // JiraAnimatedProvisioningScreen handles redirection
            const showAnimatedProvisioningScreen =
                FeatureGates.getExperimentValue(
                    "redesign_jira_provisioning_screen",
                    "showAnimatedProvisioningScreen",
                    false,
                    {
                        fireExperimentExposure: false
                    }
                );

            if (deferRedirect && showAnimatedProvisioningScreen) {
                return "jira-animated-provisioning-screen";
            }

            return "redirect";

        // "pending" status
        default:
            if (products.some(includesBitbucketOrOpsGenie)) {
                return "robots";
            } else if (products.length > 1 && products.some(includesJira)) {
                return "tips";
            } else if (
                products.length === 1 &&
                products[0] === "jira-software.ondemand"
            ) {
                // ATLAS-56326: show JiraAnimatedProvisioningScreen instead of robots
                const showAnimatedProvisioningScreen =
                    FeatureGates.getExperimentValue(
                        "redesign_jira_provisioning_screen",
                        "showAnimatedProvisioningScreen",
                        false
                    );

                if (showAnimatedProvisioningScreen) {
                    return "jira-animated-provisioning-screen";
                } else {
                    return "robots";
                }
            } else {
                return "robots";
            }
    }
};

export default useConfirmationPageType;

function includesBitbucketOrOpsGenie(text: string): boolean {
    return text.includes("bitbucket") || text.includes("opsgenie");
}

// We arent testing real product keys here, unlike what occurs in sign-up
function includesJira(text: string): boolean {
    return text.includes("jira");
}
